/**
 * Handler: view_cart event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontCart } from '../../types/storefront';
import { adaptCart } from '../../adapters/cart';
import { sendGA4Fallback, mapGA4CartItem } from '../fallbacks/ga4';
import { gtmMarketFields } from '../utils';

export function handleViewCart(
  analyzify: WindowAnalyzify,
  cart: StorefrontCart
): void {
  const adaptedCart = adaptCart(cart);

  // Sync detectedCart so CDN functions that read from window.analyzify have fresh data.
  // This makes trackViewCart self-contained — callers don't need to call setPageContext
  // first just to update cart data (e.g., when firing from side cart drawer).
  const win = window as any;
  if (win.analyzify) {
    win.analyzify.detectedCart = adaptedCart;
  }

  // GA4 - CDN's gaViewCart is cart-page gated, fall back to direct gtag
  if (typeof analyzify.gaViewCart === 'function') {
    analyzify.gaViewCart(adaptedCart);
  } else {
    const ga4Items = (adaptedCart.items || []).map((item: any, i: number) =>
      mapGA4CartItem(analyzify, item, i)
    );
    sendGA4Fallback(analyzify, 'view_cart', {
      currency: analyzify.currency || 'USD',
      value: analyzify.formatPrice?.(adaptedCart.total_price, true) ?? adaptedCart.total_price / 100,
      total_quantity: adaptedCart.item_count || 0,
      total_items: adaptedCart.items?.length || 0,
      items: ga4Items,
    });
  }

  // Facebook
  if (typeof analyzify.fbViewCart === 'function') {
    analyzify.fbViewCart(adaptedCart);
  }

  // GTM - CDN doesn't have gtmViewCart, push to dataLayer directly
  if (typeof analyzify.gtmViewCart === 'function') {
    analyzify.gtmViewCart(adaptedCart);
  } else if (Array.isArray((window as any).dataLayer)) {
    const props = analyzify.properties;
    if (props?.GTM?.status) {
      (window as any).dataLayer.push({ ecommerce: null });
      (window as any).dataLayer.push({
        event: 'ee_view_cart',
        ss_event_id: analyzify.eventId.get('view_cart') || null,
        ecommerce: {
          analyzify_source: 'advanced',
          implementation_type: 'extension',
          currency: analyzify.currency || adaptedCart.currency || 'USD',
          value: analyzify.formatPrice?.(adaptedCart.total_price, true) ?? adaptedCart.total_price / 100,
          total_quantity: adaptedCart.item_count || 0,
          total_item: adaptedCart.items?.length || 0,
          cart_id: analyzify.cart_id || null,
          items: adaptedCart.items?.map((item: any, index: number) => ({
            id: analyzify.getShopifyId?.(
              analyzify.feed_region || '',
              item.product_id,
              item.variant_id,
              item.sku
            ) || null,
            item_id: item.product_id?.toString() || null,
            item_name: item.product_title?.trim() || item.title?.trim() || null,
            price: analyzify.formatPrice?.(item.price, true) ?? item.price / 100,
            item_category: item.product_type || null,
            item_brand: item.vendor || null,
            item_handle: item.handle || null,
            item_barcode: null,
            discount: item.line_level_total_discount ? (analyzify.formatPrice?.(item.line_level_total_discount, true) || 0) : 0,
            item_variant: item.variant_title?.trim() || null,
            item_variant_id: item.variant_id?.toString() || null,
            item_sku: item.sku || null,
            index,
            quantity: item.quantity || 1,
          })) || [],
        },
        ...gtmMarketFields(analyzify),
      });
    }
  }

  // Bing
  if (typeof analyzify.bingViewCart === 'function') {
    analyzify.bingViewCart(adaptedCart);
  }

  // Criteo
  if (typeof analyzify.criteoViewCart === 'function') {
    analyzify.criteoViewCart(adaptedCart);
  }
}
